/**
 * The paid-funnel conversions of a wizard checkout, shared by every surface that hosts one: the
 * desktop steps (v0 payment, v3 finish) and the mobile flows (v0 MobilePaymentFlow, v3
 * MobileFinishFlow). One definition, so a surface cannot ship without them or count differently.
 *
 * Both hooks report to the consent-gated trackers (GA4 + Facebook + Reddit) and to the first-party
 * Pulsar beacon, like the listeners in `store/analyticsConversionsListener.ts`.
 */
import React from 'react';

import { trackInitiateCheckout, trackPurchase, trackSubscribe, trackOnce } from './conversions';
import { pulsarEvent } from './pulsar';

/** The part of a flow's `Billing` the conversions read (v0 and v3 share the shape). */
type ConversionBilling = { total: number; currency: string; productFeatures: number } | null;

// Sessions whose checkout start was already reported in this page load. A per-mount ref alone
// re-counts every remount: a phone rotating across the mobile breakpoint swaps the desktop step
// and the mobile flow, and a step left and re-entered mounts again.
const checkoutBegun = new Set<string>();

/**
 * InitiateCheckout / begin_checkout. Returns the trigger: call it when the checkout opens. Fires
 * once per session per page load (once per mount while there is no session id), so reopening the
 * sheet, leaving and re-entering the step, or a layout swap does not count again. Waits for
 * billing: a start reported with no amount cannot be corrected afterwards.
 */
export function useBeginCheckout(billing: ConversionBilling, sessionId: string | null): () => void {
  const trackedRef = React.useRef(false);
  return React.useCallback(() => {
    if (trackedRef.current || !billing) return;
    if (sessionId && checkoutBegun.has(sessionId)) return;
    trackedRef.current = true;
    if (sessionId) checkoutBegun.add(sessionId);
    const { total: value, currency } = billing;
    trackInitiateCheckout({ value, currency, contentIds: sessionId ? [sessionId] : [] });
    pulsarEvent('begin_checkout', { value, currency });
  }, [billing, sessionId]);
}

/**
 * Purchase (+ Subscribe when the plan has recurring monthly features) once `settled`, i.e. once the
 * money has ARRIVED: a cleared card charge, nothing owed, or a paid invoice. Never the buyer's word
 * on a bank transfer. `trackOnce` keys on the session, so the websocket success, a 3DS-redirect
 * reload and a desktop/mobile switch in the same browser count exactly one purchase.
 */
export function usePurchaseConversion(settled: boolean, billing: ConversionBilling, sessionId: string | null): void {
  // `billing` is null while the session reloads (the acknowledge re-reads it, and that read can
  // fail). Reporting in that window would lock in a purchase of 0 for good, so the last billing
  // known for THIS session stands in; only a checkout that never had one waits. Declared before
  // the purchase effect: effects run in order, so a commit that brings billing updates it first.
  const lastBilling = React.useRef<{ sessionId: string | null; billing: ConversionBilling }>({ sessionId, billing });
  React.useEffect(() => {
    if (billing) lastBilling.current = { sessionId, billing };
  }, [billing, sessionId]);
  React.useEffect(() => {
    const known = billing ?? (lastBilling.current.sessionId === sessionId ? lastBilling.current.billing : null);
    if (!settled || !sessionId || !known) return;
    const { total: value, currency, productFeatures: monthly } = known;
    trackOnce(`${sessionId}:purchase`, () => {
      trackPurchase({ transactionId: sessionId, value, currency, contentIds: [sessionId] });
      pulsarEvent('purchase', { transaction_id: sessionId, value, currency });
      if (monthly > 0) {
        trackSubscribe({ value: monthly, currency });
        pulsarEvent('subscribe', { value: monthly, currency });
      }
    });
  }, [settled, sessionId, billing]);
}
